import { useCallback, useState, ReactNode } from "react"
import { Alert, AlertTitle, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { UserMeetingsReceipt } from "../../../../../../modules/nonAdminUser/reports/MeetingsReceipt/UserMeetingsReceipt"
import { TranslationKey } from "../../../../../../modules/localization/translations"

const useStyles = makeStyles()((theme) => ({
    card: {
        background: theme.palette.common.white,
        color: theme.palette.tailwind.slate[950],
        padding: "2rem 3rem",
        textAlign: "left",
    },
    title: {
        fontWeight: "bold",
    },
    intro: {
        color: theme.palette.text.secondary,
    },
    callout: {
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        background: theme.palette.tailwind.slate[50],
        borderRadius: ".75rem",
        padding: "1rem 1.25rem",
        alignItems: "flex-start",
        "& .MuiAlert-icon": {
            color: theme.palette.tailwind.slate[700],
            paddingTop: ".125rem",
        },
        "& .MuiAlertTitle-root": {
            margin: 0,
        },
        "& .MuiAlert-message": {
            padding: 0,
        },
    },
    calloutTitleText: {
        fontWeight: 700,
    },
    orderIdButton: {
        appearance: "none",
        background: "transparent",
        border: "none",
        padding: 0,
        font: "inherit",
        color: theme.palette.tailwind.blue[600],
        textDecoration: "underline",
        cursor: "pointer",
        "&:hover": {
            color: theme.palette.tailwind.blue[700],
        },
        "&:focus-visible": {
            outline: `2px solid ${theme.palette.tailwind.blue[300]}`,
            outlineOffset: ".125rem",
            borderRadius: ".25rem",
        },
    },
    orderIdText: {
        color: theme.palette.tailwind.blue[600],
        textDecoration: "none",
    },
}))

type CreditNoteOverlayProps = Readonly<{
    orderId?: number
}>

type Classes = ReturnType<typeof useStyles>["classes"]

type CreditNoteCalloutProps = Readonly<{
    classes: Classes
    titleId: TranslationKey
    titleDefault: string
    bodyId: TranslationKey
    bodyDefault: ReactNode
}>

function CreditNoteCallout({ classes, titleId, titleDefault, bodyId, bodyDefault }: CreditNoteCalloutProps) {
    return (
        <Alert severity="info" className={classes.callout}>
            <AlertTitle>
                <LocalizedStrict id={titleId}>
                    <Typography variant="body1" className={classes.calloutTitleText}>
                        {titleDefault}
                    </Typography>
                </LocalizedStrict>
            </AlertTitle>
            <LocalizedStrict id={bodyId}>
                <Typography variant="body2">{bodyDefault}</Typography>
            </LocalizedStrict>
        </Alert>
    )
}

export function CreditNoteOverlay({ orderId }: CreditNoteOverlayProps) {
    const { classes } = useStyles()
    const [showReceiptModal, setShowReceiptModal] = useState(false)

    const showReceipt = useCallback(() => {
        if (!orderId) return
        setShowReceiptModal(true)
    }, [orderId])

    const handleCloseReceipt = () => {
        setShowReceiptModal(false)
    }

    return (
        <Stack gap="1.75rem" className={classes.card}>
            <Stack gap=".5rem">
                <LocalizedStrict id="create-order-credit-note-overlay-title">
                    <Typography variant="h4" className={classes.title}>
                        Credit note
                    </Typography>
                </LocalizedStrict>
                <LocalizedStrict
                    id="create-order-credit-note-overlay-intro-with-order-id"
                    vars={{ orderId: orderId ? String(orderId) : "-" }}
                    elems={{
                        orderIdLink:
                            typeof orderId === "number" ? (
                                <button type="button" className={classes.orderIdButton} onClick={showReceipt} />
                            ) : (
                                <span className={classes.orderIdText} />
                            ),
                    }}
                >
                    <Typography className={classes.intro}>
                        You are creating a credit note for order ID #{orderId}.
                    </Typography>
                </LocalizedStrict>
            </Stack>
            <CreditNoteCallout
                classes={classes}
                titleId="create-order-credit-note-overlay-callout-title"
                titleDefault="How to make a credit note:"
                bodyId="create-order-credit-note-overlay-callout-body"
                bodyDefault="Adjust the products in the cart. The items currently in the cart are being credited. You can add your own order line if needed."
            />
            {showReceiptModal && (
                <UserMeetingsReceipt open={showReceiptModal} id={orderId} onClose={handleCloseReceipt} />
            )}
        </Stack>
    )
}
